import React, { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { Navigate } from 'react-router';

import { useToast } from '../contexts/ToastContext';
import { UpdateUserNotificationPreferencesDocument } from '../graphql/mutations/__generated__/UpdateUserNotificationPreferences';
import { UserNotificationPreferencesDocument } from '../graphql/queries/__generated__/UserNotificationPreferences';
import trackEvent from '../helpers/trackEvent';
import { ERROR_PAGE_ROUTE } from '../routes/routes';
import { Divider } from './Divider';
import { Loader } from './Loader';
import NotificationPreferenceCard from './NotificationPreferenceCard';

export interface INotificationCategory {
  categoryKey: string;
  label?: string;
  description?: string;
  enabled: boolean;
}

export interface INotificationPreferencesProps {
  userId?: string;
  withoutHeader?: boolean;
}

const NOTIFICATION_CATEGORIES = [
  {
    categoryKey: 'InitialLeadNotifications',
    label: 'Offer',
    description: 'Get notified when an offer has been viewed, sent, expired, denied, or refreshed.',
  },
  {
    categoryKey: 'InspectionNotifications',
    label: 'Assessment',
    description: 'Get notified when your lead completes their exterior or interior assessment.',
  },
  {
    categoryKey: 'FinalOfferNotifications',
    label: 'Final Offer',
    description: 'Get notified when your lead receives their final offer.',
  },
  {
    categoryKey: 'PostOfferNotifications',
    label: 'Contract & Closing',
    description:
      'Get notified when a contract is signed, when contingencies are released, and other closing milestones.',
  },
];

const NotificationPreferences: React.FC<INotificationPreferencesProps> = (
  props: INotificationPreferencesProps,
) => {
  const { userId } = props;
  const [localNotificationPreferences, setLocalNotificationPreferences] = useState<
    Array<INotificationCategory>
  >([]);
  const [originalNotificationPreferences, setOriginalNotificationPreferences] = useState<
    Array<INotificationCategory>
  >([]);
  const [updateButtonEnabled, setUpdateButtonEnabled] = useState<boolean>(false);

  const { showToast } = useToast();

  const {
    data: queryData,
    loading: queryLoading,
    error: queryError,
  } = useQuery(UserNotificationPreferencesDocument, {
    fetchPolicy: 'no-cache',
    variables: { userId },
  });

  const [
    updateNotificationPreferencesMutation,
    { loading: mutationLoading, error: mutationError, data: mutationData },
  ] = useMutation(UpdateUserNotificationPreferencesDocument);
  const handleSubmit = async () => {
    trackEvent('click', 'update-notification-preferences');
    await updateNotificationPreferencesMutation({
      variables: {
        params: {
          userId,
          notificationPreferences: localNotificationPreferences.map((preference) => {
            return {
              categoryKey: preference.categoryKey,
              enabled: preference.enabled,
            };
          }),
        },
      },
    });
  };

  useEffect(() => {
    if (mutationData || mutationError) {
      const toastMessage = mutationData
        ? 'Your email preferences have been updated.'
        : 'Unable to update email preferences. Please try again.';
      showToast({
        type: mutationData ? 'info' : 'error',
        message: toastMessage,
        autoCloseMs: 5000,
      });
    }
  }, [mutationData, mutationError, showToast]);

  useEffect(() => {
    if (queryData || mutationData) {
      const preferences: Array<INotificationCategory> = mutationData
        ? mutationData.updateUserNotificationPreferences.notificationPreferences
        : queryData!.userNotificationPreferences.notificationPreferences;
      // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
      setOriginalNotificationPreferences(
        NOTIFICATION_CATEGORIES.filter((category) => {
          return preferences.find((preference) => preference.categoryKey === category.categoryKey);
        }).map((category) => {
          const preference = preferences.find(
            (preference) => preference.categoryKey === category.categoryKey,
          );
          return {
            categoryKey: category.categoryKey,
            label: category.label,
            description: category.description,
            enabled: preference!.enabled,
          };
        }),
      );
    }
  }, [queryData, mutationData]);

  const [prevOriginal, setPrevOriginal] = useState(originalNotificationPreferences);
  if (prevOriginal !== originalNotificationPreferences) {
    setPrevOriginal(originalNotificationPreferences);
    setLocalNotificationPreferences(originalNotificationPreferences);
  }

  const updateNotificationCategories = (categoryKey: string, enabled: boolean) => {
    setLocalNotificationPreferences((notificationPreferences) => {
      const preferences = notificationPreferences.map((preference) => {
        if (preference.categoryKey === categoryKey) {
          return {
            ...preference,
            enabled,
          };
        }

        return { ...preference };
      });
      return preferences;
    });
  };

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
    setUpdateButtonEnabled(
      JSON.stringify(originalNotificationPreferences) !==
        JSON.stringify(localNotificationPreferences),
    );
    // eslint-disable-next-line
  }, [localNotificationPreferences]);

  if (queryLoading) {
    return <Loader size="small" />;
  }

  if (queryError) {
    return <Navigate to={ERROR_PAGE_ROUTE} replace />;
  }

  return (
    <>
      {!props.withoutHeader && (
        <Col className="self-center">
          <Text as="h2" className="text-subheader-xs py-5 text-center">
            Email Preferences
          </Text>
          <Col className="hidden md:block">
            <Divider />
          </Col>
        </Col>
      )}
      <Col className="max-w-145 gap-2.5 self-center px-6 pt-5 pb-16">
        <Text as="p" className="text-body-md py-6">
          Email preferences can be set for each phase of the Opendoor selling process. You can
          toggle different phases based on what you'd like to get notified about.
        </Text>
        {localNotificationPreferences.map((preference, index) => (
          <NotificationPreferenceCard
            key={index}
            notificationPreference={preference}
            updateNotificationCategories={updateNotificationCategories}
          />
        ))}

        <Button
          onClick={handleSubmit}
          disabled={!updateButtonEnabled || mutationLoading}
          loading={mutationLoading}
          analyticsName="partnership-dashboard-fe-update-email-preferences"
          variant="primary"
          className="w-full"
          aria-label="Update email preferences"
        >
          Update email preferences
        </Button>
      </Col>
    </>
  );
};

export default NotificationPreferences;
